import React, { useState, useRef, useEffect } from 'react';
import { Icons } from '../../../components/ui/Icons';

interface ChatInputProps {
  onSend: (message: string, file?: File) => void;
  onCancel?: () => void;
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  onOpenIntegrations?: () => void;
  onOpenSchedules?: (prefill?: string) => void;
  isScheduleMode?: boolean;
  onToggleScheduleMode?: () => void;
  variant?: 'chat' | 'welcome';
}

const ChatInput: React.FC<ChatInputProps> = ({ 
  onSend, 
  onCancel, 
  value, 
  onChange, 
  disabled, 
  onOpenIntegrations,
  onOpenSchedules,
  isScheduleMode = false,
  onToggleScheduleMode,
  variant = 'welcome' 
}) => {
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  useEffect(() => {
    if (fileError) {
      const timer = setTimeout(() => setFileError(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [fileError]);

  // Click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (disabled && onCancel) {
        onCancel();
        return;
    }
    if (value.trim() || selectedFile) {
        onSend(value, selectedFile || undefined);
        
        // Clear state after sending
        setSelectedFile(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    }
  };

  const handleFileClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // 5MB Limit
      if (file.size > 5 * 1024 * 1024) {
        setFileError("File size must be under 5MB");
        if (fileInputRef.current) fileInputRef.current.value = '';
        return;
      }

      // Allowed types: PDF, DOCS, Image, Excel
      const allowedExtensions = ['.pdf', '.doc', '.docx', '.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg', '.xls', '.xlsx'];
      const fileName = file.name.toLowerCase();
      const isValidExtension = allowedExtensions.some(ext => fileName.endsWith(ext));

      if (!isValidExtension) {
        setFileError("Please upload PDF, Word, Excel, or Images only");
        if (fileInputRef.current) fileInputRef.current.value = '';
        return;
      }

      setFileError(null);
      setSelectedFile(file);
    }
  };

  const removeFile = () => {
      setSelectedFile(null);
      if (fileInputRef.current) {
          fileInputRef.current.value = '';
      }
  };

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.style.height = 'auto';
      inputRef.current.style.height = `${inputRef.current.scrollHeight}px`;
    }
  }, [value]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e as any);
    }
  };

  useEffect(() => {
    if (!disabled && inputRef.current) {
      inputRef.current.focus();
    }
  }, [disabled]);

  return (
    <div className="w-full max-w-3xl">
      <form 
        onSubmit={handleSubmit} 
        className="w-full relative group transition-all duration-300 border border-gray-100 dark:border-gray-800/80 shadow-sm hover:shadow-md bg-white dark:bg-[#1a1a1a] rounded-full p-2 pl-3 pr-2 focus-within:border-gray-200 dark:focus-within:border-gray-700 flex flex-col gap-2"
      >
        {/* Hidden File Input */}
        <input 
            type="file" 
            ref={fileInputRef} 
            className="hidden" 
            onChange={handleFileChange} 
            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif,.webp,.svg,.xls,.xlsx"
        />

        {/* Error Notification */}
        {fileError && (
            <div className="absolute top-2 left-1/2 -translate-x-1/2 z-50 bg-red-500 text-white px-4 py-2 rounded-full shadow-lg text-xs font-medium animate-in fade-in slide-in-from-top-2">
                {fileError}
            </div>
        )}

        {/* Selected File UI (Stacked top) */}
        {selectedFile && (
            <div className="flex items-center gap-2 self-start bg-gray-50 dark:bg-[#212121] border border-gray-100 dark:border-gray-800 rounded-xl px-3 py-1.5 animate-in fade-in slide-in-from-bottom-2 mt-1 ml-9">
                <div className="w-6 h-6 rounded-md bg-white dark:bg-[#262626] border border-gray-200 dark:border-gray-800 flex items-center justify-center shrink-0">
                    <Icons.Paperclip className="w-3.5 h-3.5 text-gray-500 dark:text-gray-400" />
                </div>
                <div className="flex flex-col min-w-0">
                    <span className="text-xs font-sans font-medium text-gray-700 dark:text-gray-300 max-w-[150px] md:max-w-[200px] truncate block">
                        {selectedFile.name}
                    </span>
                    <span className="text-[10px] font-sans text-gray-400 dark:text-gray-500 block">
                        {(selectedFile.size / 1024).toFixed(1)} KB
                    </span>
                </div>
                <button 
                    type="button"
                    onClick={removeFile}
                    className="ml-2 p-0.5 hover:bg-gray-200 dark:hover:bg-gray-850 rounded-full text-gray-500 dark:text-gray-400 hover:text-red-500 dark:hover:text-red-400 transition-colors shrink-0"
                    title="Remove file"
                >
                    <Icons.X className="w-3.5 h-3.5" />
                </button>
            </div>
        )}

        {/* Main Row */}
        <div className="flex items-center w-full gap-2">
            
            {/* Action Menu (+ Button) */}
            <div ref={dropdownRef} className="relative shrink-0 flex items-center">
              <button
                type="button"
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className="w-8 h-8 rounded-full flex items-center justify-center text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-[#262626] transition-all active:scale-95 cursor-pointer"
                title="More actions"
              >
                <Icons.Plus className="w-5 h-5" />
              </button>
              
              {/* Dropdown Menu */}
              {isDropdownOpen && (
                <div className="absolute bottom-10 left-0 z-50 bg-white dark:bg-[#212121] border border-gray-200 dark:border-gray-800 rounded-2xl shadow-xl py-1.5 min-w-[200px] flex flex-col gap-0.5 animate-in fade-in slide-in-from-bottom-2 duration-200">
                  <button
                    type="button"
                    onClick={() => {
                      handleFileClick();
                      setIsDropdownOpen(false);
                    }}
                    className="w-full text-left px-3.5 py-2.5 text-xs font-sans font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-[#2d2d2d] hover:text-black dark:hover:text-white transition-all flex items-center gap-2.5"
                  >
                    <Icons.Paperclip className="w-4 h-4 text-gray-400" />
                    Upload a file
                  </button>
                  
                  {onToggleScheduleMode && (
                    <button
                      type="button"
                      onClick={() => {
                        onToggleScheduleMode();
                        setIsDropdownOpen(false);
                      }}
                      className={`w-full text-left px-3.5 py-2.5 text-xs font-sans font-medium transition-all flex items-center gap-2.5 ${
                        isScheduleMode
                          ? 'text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-950/30 hover:bg-blue-100 dark:hover:bg-blue-950/50'
                          : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-[#2d2d2d] hover:text-black dark:hover:text-white'
                      }`}
                    >
                      <Icons.CalendarClock className={`w-4 h-4 ${isScheduleMode ? 'text-blue-500 dark:text-blue-400' : 'text-gray-400'}`} />
                      {isScheduleMode ? 'Schedule mode on' : 'Schedule task'}
                    </button>
                  )}
                  
                  {onOpenIntegrations && (
                    <button
                      type="button"
                      onClick={() => {
                        onOpenIntegrations();
                        setIsDropdownOpen(false);
                      }}
                      className="w-full text-left px-3.5 py-2.5 text-xs font-sans font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-[#2d2d2d] hover:text-black dark:hover:text-white transition-all flex items-center gap-2.5"
                    >
                      <Icons.Sliders className="w-4 h-4 text-gray-400" />
                      Connect Apps
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Schedule Mode Badge */}
            {isScheduleMode && (
              <button
                type="button"
                onClick={onToggleScheduleMode}
                className="shrink-0 flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-blue-50 dark:bg-blue-950/40 border border-blue-200 dark:border-blue-800/50 text-blue-600 dark:text-blue-400 text-[11px] font-sans font-medium hover:bg-blue-100 dark:hover:bg-blue-900/40 transition-all active:scale-95 cursor-pointer group"
                title="Click to turn off schedule mode"
              >
                <Icons.CalendarClock className="w-3.5 h-3.5" />
                <span>Schedule</span>
                <Icons.X className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity -mr-0.5" />
              </button>
            )}

            {/* Input Textarea */}
            <textarea
                ref={inputRef}
                rows={1}
                value={value}
                onKeyDown={handleKeyDown}
                onChange={(e) => onChange(e.target.value)}
                disabled={disabled}
                placeholder={isScheduleMode ? 'Describe your scheduled task . . .' : 'Ask Neura to automate . . .'}
                className="flex-1 bg-transparent border-none text-black dark:text-white placeholder-gray-400 dark:placeholder-gray-500 text-sm md:text-base font-sans font-normal focus:outline-none focus:ring-0 resize-none overflow-y-auto min-h-[36px] max-h-[120px] custom-scrollbar py-2 px-1 leading-relaxed"
            />

            {/* Send or Cancel Button */}
            <button
              type="submit"
              className={`
                w-8 h-8 md:w-9 h-9 rounded-full flex items-center justify-center transition-all duration-200 shrink-0
                ${disabled 
                  ? 'bg-black text-white hover:bg-gray-800 dark:bg-white dark:text-black dark:hover:bg-gray-200 active:scale-95 shadow-sm' 
                  : (value.trim() || selectedFile)
                    ? 'bg-[#2f2f2f] hover:bg-black dark:bg-white dark:hover:bg-gray-100 text-white dark:text-black hover:scale-105 active:scale-95 shadow-md cursor-pointer' 
                    : 'bg-gray-50 border border-gray-100/50 text-gray-300 cursor-not-allowed dark:bg-[#212121] dark:border-transparent dark:text-gray-600'}
              `}
            >
              {disabled ? <Icons.X className="w-4 h-4" /> : <Icons.ArrowUp className="w-4 h-4" />}
            </button>
        </div>
      </form>
    </div>
  );
};

export default ChatInput;
